import { useState } from 'react';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import type { WorkItemCard } from '../lib/types';
import { formatDateTime } from '../lib/dates';
import { Linkified } from '../components/Linkified';

interface Props {
  item: WorkItemCard;
  editable: boolean;
  /**
   * Whether this person may RESOLVE — which puts the card in Done, and is therefore the
   * board's permission rather than the work's. A customer closes what we have resolved; they
   * do not resolve it themselves.
   */
  canResolve: boolean;
  onChanged: () => void | Promise<void>;
}

/**
 * Resolve, then close — Halo's two steps rather than one "Done". We say it is fixed and what
 * we did; the customer agrees it is finished. Closed work leaves the board without being
 * deleted, and anything that comes back is reopened rather than raised again, so the history
 * of a recurring fault stays in one place.
 */
export function ResolutionPanel({ item, editable, canResolve, onChanged }: Props) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ask = useAsk();

  const resolved = !!item.completedAt;
  const closed = !!item.closedAt;

  async function act(path: string, body?: unknown) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/work-items/${item.id}/${path}`, body);
      setNote('');
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card mt-2">
      <div className="card-head">
        <h3>What was fixed</h3>
        <span className="row" style={{ gap: 6 }}>
          {item.reopenCount > 0 && (
            <span className="badge badge-warn" title="How many times this has come back">
              reopened {item.reopenCount}×
            </span>
          )}
          {closed ? (
            <span className="badge">closed</span>
          ) : resolved ? (
            <span className="badge badge-ok">resolved</span>
          ) : (
            <span className="badge">open</span>
          )}
        </span>
      </div>

      <div className="card-pad">
        {error && <div className="callout">{error}</div>}

        {item.resolution && (
          <div
            className="card card-pad"
            style={{ background: 'var(--surface-sunken)', whiteSpace: 'pre-wrap', fontSize: 13, marginBottom: 14 }}
          >
            <Linkified text={item.resolution} />
          </div>
        )}

        {resolved && (
          <div className="stack-sm" style={{ marginBottom: 14 }}>
            <div className="row-between">
              <span className="muted">Resolved</span>
              <span className="mono">{formatDateTime(item.completedAt!)}</span>
            </div>
            {closed && (
              <div className="row-between">
                <span className="muted">Closed</span>
                <span className="mono">{formatDateTime(item.closedAt!)}</span>
              </div>
            )}
          </div>
        )}

        {!editable ? (
          <p className="dim" style={{ fontSize: 12, margin: 0 }}>
            Your role can read this but not change it.
          </p>
        ) : !resolved && !closed ? (
          canResolve ? (
          <>
            {/*
              NEVER REQUIRED, AND IT USED TO BE.
              
              The old field demanded a note on anything with a service level, which caught work
              that was never broken and put the question to whoever happened to be finishing it.
              It is worth recording and not worth blocking on: the person who knows what fixed it
              is the person who fixed it, and they can write it here whenever they get to it.
            */}
            <label className="field">
              <span className="field-label">What was fixed (optional)</span>
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="The retry now backs off instead of firing twice"
              />
              <span className="field-hint">
                For whoever meets the same thing next month. Leave it empty if there is nothing to
                say — work that was made rather than repaired usually has nothing.
              </span>
            </label>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => act('resolve', { resolution: note.trim() || undefined })}
            >
              {busy ? 'Saving…' : 'Mark resolved'}
            </button>
          </>
          ) : (
            /* Said rather than shown as a dead button: resolving is ours, and they will be
               able to close it once we have. */
            <p className="dim" style={{ fontSize: 12, margin: 0 }}>
              We mark this resolved once it is done. You will be able to close it then.
            </p>
          )
        ) : (
          <div className="row wrap" style={{ gap: 8 }}>
            {/*
              `!resolved && !closed` above, not just `!resolved`.

              A closed item that had lost its resolution — which a drag off the Done column
              used to do — fell into the branch above and was offered "Mark resolved". The API
              refuses that with "This is closed. Reopen it first", and Reopen lives down here,
              so the task had no way out of its own state. Closed always reaches these buttons
              now, whatever its resolution says.
            */}
            {!closed && resolved && (
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act('close')}>
                Close it
              </button>
            )}
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy}
              onClick={async () => {
                const reason = await ask.prompt({
                  title: 'Why is this being reopened?',
                  body: 'It counts as a reopen either way — this is what the history will say about it.',
                  label: 'What brought it back',
                  placeholder: 'Came back on the next statement run',
                  confirmLabel: 'Reopen it',
                  minLength: 3,
                });
                if (reason === null) return;
                if (!reason.trim()) {
                  setError('A reopen needs a reason, so the history reads.');
                  return;
                }
                void act('reopen', { reason: reason.trim() });
              }}
            >
              Reopen
            </button>
            {!closed && (
              <span className="dim" style={{ fontSize: 12, alignSelf: 'center' }}>
                Closing takes it off the board. It is kept, and can still be reopened.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
